import React, { useCallback, useState } from 'react';
import {
    Box,
    PauseFilled,
    PlayFilled,
    Progress,
    Button,
    ButtonProps,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { InstrumentationOptions } from 'types';
import { SamplePackArtwork } from '../SamplePackArtwork';

export interface DemoButtonProps extends Omit<ButtonProps, 'onClick'> {
    onClick: (e: React.MouseEvent) => void;
    playing: boolean;
    packArtworkUrl?: string;
    packName?: string;
    instrumentationOptions: InstrumentationOptions;
    children?: React.ReactElement | React.ReactElement[];
}

export const DemoButton = ({
    onClick,
    playing,
    isLoading,
    packArtworkUrl,
    packName,
    instrumentationOptions,
    ...props
}: DemoButtonProps): JSX.Element => {
    const PauseLabel = t`Pause`;
    const PlayLabel = t`Play`;
    const [focused, setFocused] = useState(false);

    const onPlayClick = useCallback(
        (e: React.MouseEvent) => {
            onClick(e);
            setFocused(true);
            if (!focused) {
                (e.target as HTMLButtonElement).focus();
            }
        },
        [onClick, setFocused, focused],
    );

    const getPlayButtonIcon = (): JSX.Element => {
        if (!playing && !focused) {
            return (
                <SamplePackArtwork
                    sampleUrl={packArtworkUrl ?? ''}
                    samplePackName={packName ?? ''}
                />
            );
        }
        const PlayIcon = playing ? PauseFilled : PlayFilled;

        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="md"
                height="md"
            >
                <Box
                    width="md"
                    height="md"
                    justifyContent={'center'}
                    alignContent={'center'}
                    borderRadius="100%"
                    backgroundColor="surface.medium"
                >
                    {isLoading ? (
                        <Progress width="md" height="md" />
                    ) : (
                        <PlayIcon />
                    )}
                </Box>
            </Box>
        );
    };

    const onButtonBlur = useCallback(() => setFocused(false), [setFocused]);

    const buttonLabel = playing ? PauseLabel : PlayLabel;

    return (
        <Button
            icon={getPlayButtonIcon}
            onClick={onPlayClick}
            aria-label={buttonLabel}
            data-testid="samples-table-listen-demo-btn"
            onBlur={onButtonBlur}
            textDecoration="none"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-start"
            {...props}
        />
    );
};
